import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../states/hooks";
import {
  fetchStudents,
  createStudent,
  updateStudent,
  deleteStudent,
  clearError as clearStudentsError,
  selectStudents,
  selectStudentsLoading,
  selectStudentsError,
} from "../../states/students/StudentsSlice";
import {
  fetchClass,
  selectCurrentClass,
} from "../../states/classes/ClassesSlice";
import { StudentData } from "../../model/StudentData";
import { useBase } from "../../context/BaseContext";
import EditStudentModal from "../../components/EditStudentModal";
import {
  ChevronLeftIcon,
  DeleteIcon,
  EditIcon,
  EmptyBoxIcon,
} from "../../assets/svg";
import "../dashboard/Dashboard.css";

const ClassStudentsPage: React.FC = () => {
  const { classId = "" } = useParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const students = useAppSelector(selectStudents);
  const isLoading = useAppSelector(selectStudentsLoading);
  const error = useAppSelector(selectStudentsError);
  const currentClass = useAppSelector(selectCurrentClass);
  const { showError, showConfirm, showSuccess } = useBase();

  const [modal, setModal] = useState<{
    isOpen: boolean;
    student: StudentData | null;
  }>({ isOpen: false, student: null });

  useEffect(() => {
    if (classId) {
      dispatch(fetchStudents(classId));
      dispatch(fetchClass(classId));
    }
  }, [dispatch, classId]);

  useEffect(() => {
    if (error) {
      showError("Lỗi", error);
      dispatch(clearStudentsError());
    }
  }, [error, showError, dispatch]);

  const handleSave = async (data: {
    name: string;
    resetEmbeddings?: boolean;
  }) => {
    if (modal.student) {
      const payload: any = { id: modal.student.id, name: data.name };
      if (data.resetEmbeddings) {
        payload.embedding1 = [];
        payload.embedding2 = [];
        payload.embedding3 = [];
      }
      const result = await dispatch(updateStudent(payload));
      if (updateStudent.fulfilled.match(result)) {
        showSuccess("Thành công", "Đã cập nhật học sinh");
      }
    } else {
      const result = await dispatch(
        createStudent({ classId, name: data.name }),
      );
      if (createStudent.fulfilled.match(result)) {
        showSuccess("Thành công", "Đã thêm học sinh");
      }
    }
    setModal({ isOpen: false, student: null });
  };

  const handleDelete = (s: StudentData) => {
    showConfirm(
      "Xoá học sinh",
      `Xoá học sinh "${s.name}"? Lịch sử điểm danh của em này sẽ bị xoá.`,
      async () => {
        const result = await dispatch(deleteStudent(s.id));
        if (deleteStudent.fulfilled.match(result)) {
          showSuccess("Thành công", "Đã xoá học sinh");
        }
      },
    );
  };

  return (
    <>
      <EditStudentModal
        isOpen={modal.isOpen}
        student={modal.student}
        onClose={() => setModal({ isOpen: false, student: null })}
        onSave={handleSave}
      />
      <div className="dashboard-container">
        <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 1 }}>
          <IconButton onClick={() => navigate("/classes")} size="small">
            <ChevronLeftIcon className="w-6 h-6" />
          </IconButton>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {currentClass?.name || "Lớp"} — Danh sách học sinh
          </Typography>
        </Box>

        <div className="dashboard-stats-grid">
          <div className="dashboard-stat-card">
            <div className="dashboard-stat-card-content">
              <div className="dashboard-stat-flex">
                <div className="dashboard-stat-info">
                  <dl>
                    <dt className="dashboard-stat-label">Tổng học sinh</dt>
                    <dd className="dashboard-stat-value">{students.length}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          <div />
          <Button
            variant="contained"
            color="primary"
            onClick={() => setModal({ isOpen: true, student: null })}
            sx={{
              textTransform: "none",
              height: "fit-content",
              alignSelf: "center",
            }}
          >
            + Thêm học sinh
          </Button>
        </div>

        <div className="dashboard-table-container">
          {isLoading ? (
            <div className="dashboard-loading">
              <div className="dashboard-loading-content">
                <div className="dashboard-loading-spinner" />
                <p className="dashboard-loading-text">Đang tải...</p>
              </div>
            </div>
          ) : students.length === 0 ? (
            <div className="dashboard-empty">
              <EmptyBoxIcon className="dashboard-empty-icon" />
              <h3 className="dashboard-empty-title">Chưa có học sinh</h3>
              <p className="dashboard-empty-description">
                Bấm "Thêm học sinh" để bắt đầu.
              </p>
            </div>
          ) : (
            <TableContainer component={Paper} sx={{ boxShadow: 3 }}>
              <Table aria-label="students table">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                    <TableCell sx={{ fontWeight: "bold" }}>STT</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Tên</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>
                      Face embeddings
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold" }} align="center">
                      Hành động
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {students.map((s, i) => {
                    const count = [s.embedding1, s.embedding2, s.embedding3]
                      .filter((e) => e && e.length > 0).length;
                    return (
                      <TableRow
                        key={s.id}
                        sx={{ "&:hover": { backgroundColor: "#f9f9f9" } }}
                      >
                        <TableCell>{i + 1}</TableCell>
                        <TableCell>
                          <Typography sx={{ fontWeight: 500 }}>
                            {s.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={`${count}/3`}
                            size="small"
                            color={
                              count === 3
                                ? "success"
                                : count === 0
                                  ? "default"
                                  : "warning"
                            }
                            variant={count === 0 ? "outlined" : "filled"}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            onClick={() =>
                              setModal({ isOpen: true, student: s })
                            }
                            color="primary"
                            size="small"
                            title="Sửa"
                          >
                            <EditIcon style={{ width: 20, height: 20 }} />
                          </IconButton>
                          <IconButton
                            onClick={() => handleDelete(s)}
                            color="error"
                            size="small"
                            title="Xoá"
                          >
                            <DeleteIcon style={{ width: 20, height: 20 }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </div>
      </div>
    </>
  );
};

export default ClassStudentsPage;
